/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback, useRef } from "react";
// UNCOMMENT THE LINES BELOW IN YOUR LOCAL PROJECT AFTER INSTALLING PACKAGES
import { GoogleMap, useJsApiLoader, Polyline } from "@react-google-maps/api";
import { Card, Button, Typography, Space, Tag, Spin, Alert } from "antd"; // Using Ant Design components

// --- Constants ---
const GOOGLE_MAPS_API_KEY = "AIzaSyBlsiu2kCn_5Bmd1g43Bk4Vrq7LaoEtbHk"; // REPLACE WITH YOUR ACTUAL API KEY
const MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "700px", // Adjusted height for better visibility
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
};

const DEFAULT_CENTER = {
  lat: 24.8607, // Karachi, Pakistan coordinates
  lng: 67.0011,
};

const LIBRARIES: ("drawing" | "geometry" | "places" | "visualization")[] = [
  "places",
  "geometry",
];

// --- Dummy Route Data (Example, adapt to your needs) ---
// Each route should have unique ID, a name, and points (origin, destination, waypoints)
const DUMMY_ROUTES = [
  {
    id: "route-clifton-dha",
    name: "CLIFTON - DHA Phase 6",
    color: "#8A2BE2", // Purple
    points: {
      origin: "Clifton, Karachi",
      destination: "DHA Phase 6, Karachi",
      waypoints: [{ location: "Do Darya, Karachi" }],
    },
    visible: true,
  },
  {
    id: "route-saddar-pechs",
    name: "SADDAR - PECHS",
    color: "#00BFFF", // Deep Sky Blue
    points: {
      origin: "Saddar, Karachi",
      destination: "PECHS, Karachi",
      waypoints: [{ location: "Tariq Road, Karachi" }],
    },
    visible: true,
  },
  {
    id: "route-gulshan-nazimabad",
    name: "GULSHAN - NAZIMABAD",
    color: "#32CD32", // Lime Green
    points: {
      origin: "Gulshan-e-Iqbal, Karachi",
      destination: "Nazimabad, Karachi",
      waypoints: [{ location: "Nagan Chowrangi, Karachi" }],
    },
    visible: true,
  },
  {
    id: "route-korangi-malir",
    name: "KORANGI - MALIR CANTT",
    color: "#FF4500", // Orange Red
    points: {
      origin: "Korangi, Karachi",
      destination: "Malir Cantt, Karachi",
      waypoints: [
        { location: "Model Colony, Karachi" },
        { location: "Nagan Chowrangi, Karachi" },
      ],
    },
    visible: true,
  },
];

// --- Main Map Component ---
const RoutesMap: React.FC = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const mapRef = useRef<any>(null); // Changed to any due to mock
  const directionsServiceRef = useRef<any>(null); // Changed to any due to mock

  // State to store calculated polylines and current visibility/highlight status
  const [routesData, setRoutesData] = useState<
    Array<{
      id: string;
      name: string;
      color: string;
      polyline?: google.maps.PolylineOptions; // Polyline data from Directions API
      visible: boolean;
      isActive: boolean; // For highlighting
    }>
  >([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to calculate and fetch directions for all routes
  const fetchAllRoutes = useCallback(async (service: any) => {
    // Type changed to any for mock
    setLoadingRoutes(true);
    setError(null);
    const newRoutesData = await Promise.all(
      DUMMY_ROUTES.map(async (route) => {
        try {
          const request: any = {
            // Type changed to any for mock
            origin: route.points.origin,
            destination: route.points.destination,
            waypoints: route.points.waypoints,
            travelMode: google.maps.TravelMode.DRIVING,
          };

          // Using a Promise wrapper around the callback-based route method
          const response: any = await new Promise((resolve, reject) => {
            service.route(request, (result: any, status: any) => {
              // Use mock service.route
              if (status === "OK") {
                resolve(result);
              } else {
                reject(`Directions request failed with status: ${status}`);
              }
            });
          });

          console.log("Directions API response for", route.name, ":", response);

          if (response.routes.length > 0) {
            // Convert LatLng objects to LatLngLiteral objects for Polyline path
            const path = response.routes[0].overview_path.map(
              (latLng: any) => ({
                lat: latLng.lat(),
                lng: latLng.lng(),
              })
            );

            return {
              id: route.id,
              name: route.name,
              color: route.color,
              polyline: {
                path: path,
                strokeColor: route.color,
                strokeOpacity: 0.8,
                strokeWeight: 4,
                zIndex: 1, // Default z-index
              },
              visible: route.visible,
              isActive: false,
            };
          } else {
            console.warn(`No routes found for ${route.name}`);
            return { ...route, polyline: undefined, isActive: false };
          }
        } catch (err: any) {
          console.error(`Error fetching route for ${route.name}:`, err);
          return { ...route, polyline: undefined, isActive: false };
        }
      })
    );
    setRoutesData(newRoutesData);
    setLoadingRoutes(false);
  }, []); // Dependencies: empty, because this function always uses the passed service instance and DUMMY_ROUTES

  // Callback for when map is loaded

  // Callback for when map is loaded
  const onLoad = useCallback(
    (map: any) => {
      // Type changed to any for mock
      mapRef.current = map;
      // Instantiate mock DirectionsService
      directionsServiceRef.current = new google.maps.DirectionsService();
      console.log(
        "Map loaded. DirectionsService initialized:",
        directionsServiceRef.current
      );

      // *Crucial Fix*: Call fetchAllRoutes here directly after DirectionsService is initialized
      if (directionsServiceRef.current) {
        fetchAllRoutes(directionsServiceRef.current);
      }
    },
    [fetchAllRoutes]
  ); // Add fetchAllRoutes as a dependency for onLoad

  // Callback for when map is unmounted
  const onUnmount = useCallback(() => {
    mapRef.current = null;
    directionsServiceRef.current = null;
    console.log("Map unmounted. Refs cleared.");
  }, []);

  console.log(
    "isLoaded:",
    isLoaded,
    "directionsServiceRef.current (outside onLoad):",
    directionsServiceRef.current
  );

  // Function to toggle route visibility
  const toggleRouteVisibility = useCallback((id: string) => {
    setRoutesData((prevRoutes) =>
      prevRoutes.map((route) =>
        route.id === id ? { ...route, visible: !route.visible } : route
      )
    );
  }, []);

  // Function to highlight a route (make it active)
  const highlightRoute = useCallback((id: string) => {
    setRoutesData((prevRoutes) =>
      prevRoutes.map((route) => ({
        ...route,
        isActive: route.id === id ? !route.isActive : false, // Toggle active for clicked, deactivate others
        // Ensure route.polyline exists before trying to access its properties
        polyline: route.polyline
          ? {
              ...route.polyline,
              strokeWeight: route.id === id && !route.isActive ? 6 : 4,
              strokeOpacity: route.id === id && !route.isActive ? 1.0 : 0.8,
              zIndex: route.id === id && !route.isActive ? 2 : 1,
            }
          : undefined, // If polyline was undefined, keep it undefined
      }))
    );
  }, []);

  if (loadError)
    return <Alert message="Error loading Google Maps" type="error" showIcon />;
  if (!isLoaded)
    return (
      <Spin
        tip="Loading Map..."
        size="large"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      />
    );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        padding: "20px",
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      <Typography.Title
        level={2}
        style={{ textAlign: "center", color: "#333" }}
      >
        Routes Overview
      </Typography.Title>

      <Card
        style={{
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Typography.Title level={4}>Route List</Typography.Title>
        <Space size={[8, 16]} wrap>
          {DUMMY_ROUTES.map((route) => {
            const currentRouteData = routesData.find((r) => r.id === route.id);
            const isVisible = currentRouteData?.visible ?? true;
            const isActive = currentRouteData?.isActive ?? false;

            return (
              <Tag
                key={route.id}
                color={isActive ? "blue" : isVisible ? route.color : "default"}
                onClick={() => highlightRoute(route.id)}
                closable
                onClose={(e) => {
                  e.stopPropagation(); // Prevent highlight when closing
                  toggleRouteVisibility(route.id);
                }}
                style={{
                  cursor: "pointer",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: isActive
                    ? "2px solid #1890ff"
                    : `1px solid ${isVisible ? route.color : "#d9d9d9"}`,
                  fontWeight: isActive ? "bold" : "normal",
                  fontSize: "14px",
                }}
              >
                {route.name}
              </Tag>
            );
          })}
        </Space>
        {loadingRoutes && <Spin style={{ marginLeft: "10px" }} />}
        {error && (
          <Alert
            message={error}
            type="warning"
            showIcon
            style={{ marginTop: "10px" }}
          />
        )}
      </Card>

      <div style={{ position: "relative" }}>
        <GoogleMap
          mapContainerStyle={MAP_CONTAINER_STYLE}
          center={DEFAULT_CENTER}
          zoom={10}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            zoomControl: true,
            styles: [
              // Optional: Map styles for a cleaner look
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }],
              },
              {
                featureType: "transit",
                elementType: "labels",
                stylers: [{ visibility: "off" }],
              },
            ],
          }}
        >
          {routesData.map(
            (route) =>
              route.visible &&
              route.polyline &&
              route.polyline.path && ( // Ensure path exists
                <Polyline
                  key={route.id}
                  path={route.polyline.path as any[]} // Explicitly cast to any[] for mock compatibility
                  options={{
                    strokeColor: route.polyline.strokeColor,
                    strokeOpacity: route.polyline.strokeOpacity,
                    strokeWeight: route.polyline.strokeWeight,
                    zIndex: route.polyline.zIndex,
                  }}
                />
              )
          )}
        </GoogleMap>
        <Button
          type="primary"
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            borderRadius: "8px",
            zIndex: 10,
          }}
          onClick={() => {
            // Logic to toggle 'Show all routes'
            const allVisible = routesData.every((r) => r.visible);
            setRoutesData((prevRoutes) =>
              prevRoutes.map((r) => ({ ...r, visible: !allVisible }))
            );
          }}
        >
          Show all routes
        </Button>
      </div>
    </div>
  );
};

export default RoutesMap;
